import {FULFILLED, REJECTED} from 'redux-promise-middleware';
import {put, putResolve, call, takeEvery, select, fork, all, cancel} from 'redux-saga/effects';
import {Action} from 'redux-act';
import {push} from 'connected-react-router';
import {
    GENERAL,
    INDICATION_USERS_WITH_PRODUCTS,
    LOGIN,
    sendMobileAnalyticsEvent,
} from '@optimaxdev/analytics/mobile';

import {OAUTH_COOKIE_KEY} from 'constants/app';
import {REGISTER} from 'features/menu/constants';
import {
    getToken,
    fetchSelf,
    getSocialToken,
    setError,
    getCustomer,
    setRedirect,
    getAuthOption,
} from 'reducers/oAuth/oAuth';
import {OAuthSocialType, OAuthTokenType} from 'reducers/oAuth/oAuthType';
import {cookieStore, localStore, sessionStore} from 'libs/storage/storage';
import {customer} from 'libs/user/user';
import {createEventChannel} from 'libs/eventChannel/eventChannel';
import {getCustomerData} from 'actions/customer/customer';
import {visibilityMenu} from 'actions/menu/mainMenu';
import {getCurrentPage} from 'selectors/app/app';
import {getQuoteItems} from 'actions/checkout/checkout';
import {getIsQuoteItemsFulfilled, isNotEmptyQuoteItems} from 'selectors/cart/cart';
import {setWishlist} from 'sagas/wishlist/wishlist';
import {sendAnalytics} from 'features/wizardMs/utils/sendAnalytics';
import {setUserDataToDataLayer} from 'sagas/app/app';
import {
    getWishlistCount,
    isWishlistReady as isWishlistReadySelector,
} from 'reducers/wishlist/wishlist';
import {waitForFulfilled} from 'libs/utils/utils';
import {QUOTE_ITEM, WISHLIST} from 'constants/actions';
import {POST_MESSAGE_TYPES, postMessage} from 'libs/postMessage/postMessage';

/**
 * Provides a redirect after success auth if it was set and after
 * registration without reorder url parameter that error popup on CL was not displayed
 */
export function* afterSuccessSideEffect() {
    const {redirect} = yield select(getCustomer);
    const authOption = yield select(getAuthOption);

    if (redirect) {
        yield put(push(authOption === REGISTER ? redirect.split('&reorder=1').join('') : redirect));
        yield put(setRedirect(''));
    }
}

/**
 * If there is a token fetch self on page loading and set wishlist
 */
export function* fetchSelfIfToken() {
    const tokenData = yield call([cookieStore, 'get'], OAUTH_COOKIE_KEY, {});
    if (tokenData.access_token) {
        yield put(fetchSelf());
    }
    yield call(setWishlist, Boolean(tokenData.access_token));
}

/**
 * Dispatches actions needed after successful auth
 */
export function* putSuccessAuthActions() {
    yield put(fetchSelf());
    yield putResolve(getCustomerData());
    yield put(visibilityMenu(false));
    yield call(setWishlist, true);
    yield call(afterSuccessSideEffect);
}

/**
 * Send analytics
 * @param {string} label - name of indicators
 */
export function* sendAnalyticsHelper(label: string) {
    const sessionData = yield call([sessionStore, 'get'], INDICATION_USERS_WITH_PRODUCTS);
    if (label !== sessionData) {
        yield call(
            sendMobileAnalyticsEvent,
            GENERAL.GOT_SAVED_ITEMS,
            label as 'wishlist' | 'cart' | 'Wishlist and Cart',
            false,
        );
    }
}

/**
 * Send analytics if in cart or wishlist there are products
 */
export function* sendAnalyticsWithIndicationOfPresenceProducts() {
    yield call(waitForFulfilled, isWishlistReadySelector, WISHLIST.SET_WISHLIST_ITEMS);
    yield call(waitForFulfilled, getIsQuoteItemsFulfilled, `${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`);

    const [isNotEmptyWishlist, isNotEmptyCart] = yield all([
        Boolean(yield select(getWishlistCount)),
        yield select(isNotEmptyQuoteItems),
    ]);

    if (!isNotEmptyWishlist && !isNotEmptyCart) yield cancel();

    const productInCartOrWishlist =
        (isNotEmptyWishlist && 'Wishlist') || (isNotEmptyCart && 'Cart');
    const label =
        isNotEmptyWishlist && isNotEmptyCart ? 'Wishlist and Cart' : productInCartOrWishlist;

    yield call(sendAnalyticsHelper, label);
}

/**
 * After receiving the token, a request is made to obtain data about the page and quoteItems
 */
export function* getCustomerDataAfterGettingToken() {
    localStore.set('loginMethod', 'Email');
    const {page} = yield select(getCurrentPage);
    if (page === 'checkout') yield put(getQuoteItems(null, {isCheckoutLogin: true}));

    yield call(putSuccessAuthActions);
    yield call(setUserDataToDataLayer);
    yield call(sendAnalyticsWithIndicationOfPresenceProducts);
}

/**
 * Gets auth token and dispatches actions on customer data and left menu close
 */
export function* fetchToken({
    payload: {email, password, type},
}: Action<{email: string; password: string; type: string}>) {
    const [error] = yield call([customer, 'fetchToken'], email, password);
    yield call(
        sendAnalytics,
        LOGIN[type === 'login' ? 'EMAIL_VALUE_LOGIN' : 'EMAIL_VALUE_SIGNUP'],
        {
            email: 'Email',
            label: error ? `Error - ${error}` : 'Success',
        },
    );
    if (error) {
        yield put(setError(error));
        return;
    }
    yield call(getCustomerDataAfterGettingToken);
}
/**
 * Get auth token for socials token and dispatches an action on customer data
 */
export function* fetchSocialToken({payload}: Action<OAuthSocialType>) {
    const [error] = yield call([customer, 'fetchTokenSocial'], payload);
    if (error) {
        yield put(setError(error));
        return;
    }
    yield call(putSuccessAuthActions);
}

/**
 * Refresh OAuth token if it is possible
 */
export function* refreshToken() {
    yield call(postMessage, {
        type: POST_MESSAGE_TYPES.REFRESH_TOKEN,
    });

    const token: OAuthTokenType = yield call([cookieStore, 'get'], OAUTH_COOKIE_KEY, {});
    yield call([cookieStore, 'remove'], OAUTH_COOKIE_KEY);

    if (token.refresh_token) {
        yield call([customer, 'refreshToken'], token.refresh_token);
        yield put(fetchSelf());
    }
}

/**
 * Root OAuth Saga
 */
export default function* oAuthSaga() {
    yield fork(fetchSelfIfToken);

    const refreshChannel = yield call(createEventChannel, 'oAuth:refreshToken');

    yield takeEvery(refreshChannel, refreshToken);
    yield takeEvery(`${fetchSelf}_${REJECTED}`, refreshToken);
    yield takeEvery(getToken, fetchToken);
    yield takeEvery(getSocialToken, fetchSocialToken);
}
